// src/pages/YTCampaignCreate.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';

// ── Languages matching backend LANGUAGE_MAP exactly ──
const LANGUAGE_OPTIONS = [
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'ta', label: 'Tamil', native: 'தமிழ்' },
  { code: 'te', label: 'Telugu', native: 'తెలుగు' },
  { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml', label: 'Malayalam', native: 'മലയാളം' },
  { code: 'bn', label: 'Bengali', native: 'বাংলা' },
  { code: 'gu', label: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'mr', label: 'Marathi', native: 'मराठी' },
  { code: 'pa', label: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'ur', label: 'Urdu', native: 'اردو' },
  { code: 'en', label: 'English', native: 'English' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'it', label: 'Italian', native: 'Italiano' },
  { code: 'pt', label: 'Portuguese', native: 'Português' },
  { code: 'ru', label: 'Russian', native: 'Русский' },
  { code: 'ja', label: 'Japanese', native: '日本語' },
  { code: 'ko', label: 'Korean', native: '한국어' },
  { code: 'zh', label: 'Chinese', native: '中文' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
];

// ── Duration filters supported by backend ──
const DURATION_OPTIONS = [
  { value: 'medium', label: 'Medium (4–20 min)' },
  { value: 'long', label: 'Long (> 20 min)' },
];

// ── Valid target video counts ──
const VALID_TARGET_VIDEOS = [5000, 10000, 15000, 20000, 25000];

const BASE_URL = process.env.REACT_APP_API_URL;
const YT_API_KEY = process.env.REACT_APP_YT_API_KEY;

const YTCampaignCreate = () => {
  const navigate = useNavigate();

  // ── Form state ──
  const [campaignName, setCampaignName] = useState('');
  const [targetVideos, setTargetVideos] = useState(25000);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedDurations, setSelectedDurations] = useState([]);
  const [keywordsText, setKeywordsText] = useState('');
  const [excludedChannels, setExcludedChannels] = useState(['']); // array of URL strings
  const [loading, setLoading] = useState(false);

  // ── Keyword count helper ──
  const parseSignals = (text) =>
    text
      .split(/[\n,]+/)
      .map(k => k.trim())
      .filter(k => k.length > 0)
      .filter((k, i, arr) => arr.indexOf(k) === i);

  const keywordCount = parseSignals(keywordsText).length;

  // ── Language checkbox toggle (max 3) ──
  const toggleLanguage = (code) => {
    setSelectedLanguages(prev => {
      if (prev.includes(code)) return prev.filter(l => l !== code);
      if (prev.length >= 3) {
        toast.error('You can select a maximum of 3 languages');
        return prev;
      }
      return [...prev, code];
    });
  };

  // ── Duration checkbox toggle ──
  const toggleDuration = (value) => {
    setSelectedDurations(prev =>
      prev.includes(value) ? prev.filter(d => d !== value) : [...prev, value]
    );
  };

  // ── Excluded channels handlers ──
  const handleChannelChange = (index, value) => {
    const updated = [...excludedChannels];
    updated[index] = value;
    setExcludedChannels(updated);
  };

  const addChannelRow = () => {
    setExcludedChannels(prev => [...prev, '']);
  };

  const removeChannelRow = (index) => {
    setExcludedChannels(prev => prev.filter((_, i) => i !== index));
  };

  // ── Validation ──
  const validate = () => {
    if (!campaignName.trim()) {
      toast.error('Campaign name is required');
      return false;
    }
    if (selectedLanguages.length === 0) {
      toast.error('Select at least one language');
      return false;
    }
    if (selectedDurations.length === 0) {
      toast.error('Select at least one duration filter');
      return false;
    }
    if (keywordCount === 0) {
      toast.error('Enter at least one signal');
      return false;
    }
    if (!VALID_TARGET_VIDEOS.includes(Number(targetVideos))) {
      toast.error('Please select a valid target video count');
      return false;
    }
    return true;
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    // Parse signals — handles newlines, commas, extra spaces, duplicates
    const keywords = parseSignals(keywordsText);

    // Parse excluded channels — filter out empty rows
    const excluded = excludedChannels
      .map(c => c.trim())
      .filter(c => c.length > 0);

    const payload = {
      name: campaignName.trim(),
      target_videos: Number(targetVideos),
      languages: selectedLanguages,
      durations: selectedDurations,
      keywords: keywords,
      excluded_channels: excluded,
    };

    try {
      const response = await axios.post(
        `${BASE_URL}/youtube_scrapper/create_campaign/`,
        payload,
        { headers: { 'X-API-KEY': YT_API_KEY } }
      );

      if (response.status === 201 || response.data.status === 'success') {
        toast.success('Campaign created successfully!');
        setTimeout(() => navigate('/yt/campaign/list'), 2000);
      } else {
        toast.error('Failed to create campaign: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // ── Clear form ──
  const handleClear = () => {
    setCampaignName('');
    setTargetVideos(25000);
    setSelectedLanguages([]);
    setSelectedDurations([]);
    setKeywordsText('');
    setExcludedChannels(['']);
    toast.success('Form cleared');
  };

  // ── Render ──
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 2500,
          style: { background: '#363636', color: '#fff', borderRadius: '8px' },
          success: { iconTheme: { primary: '#10B981', secondary: '#fff' } },
          error: { iconTheme: { primary: '#EF4444', secondary: '#fff' } },
        }}
      />

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/yt/campaign/list')}
          className="text-blue-600 hover:text-blue-800 mb-2 text-sm"
        >
          ← Back to List
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Create YouTube Campaign</h1>
        <p className="text-sm text-gray-500 mt-1">
          Configure signals, languages, and duration filters to start fetching videos.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* ── Section 1: Campaign Details ── */}
        <div className="bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Campaign Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Campaign Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="e.g., Hyundai Campaign Q2 2026"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Videos <span className="text-red-500">*</span>
              </label>
              <select
                value={targetVideos}
                onChange={(e) => setTargetVideos(Number(e.target.value))}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              >
                {VALID_TARGET_VIDEOS.map(val => (
                  <option key={val} value={val}>
                    {val.toLocaleString()} videos
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">Default is 25,000 videos per campaign</p>
            </div>
          </div>
        </div>

        {/* ── Section 2: Languages ── */}
        <div className="bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-1 text-gray-700">
            Languages <span className="text-red-500">*</span>
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Select up to <strong>3 languages</strong> to search in.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {LANGUAGE_OPTIONS.map(({ code, label, native }) => (
              <label
                key={code}
                className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition text-sm ${
                  selectedLanguages.includes(code)
                    ? 'bg-blue-50 border-blue-500 text-blue-700 font-medium'
                    : 'border-gray-200 text-gray-600 hover:border-blue-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedLanguages.includes(code)}
                  onChange={() => toggleLanguage(code)}
                  className="accent-blue-600"
                  disabled={loading || (!selectedLanguages.includes(code) && selectedLanguages.length >= 3)}
                />
                <span>
                  <span className="block text-xs font-medium">{label}</span>
                  <span className="block text-xs text-gray-400">{native}</span>
                </span>
              </label>
            ))}
          </div>
          {selectedLanguages.length > 0 && (
            <p className="mt-3 text-xs font-medium" style={{color: selectedLanguages.length === 3 ? '#16a34a' : '#2563eb'}}>
              {selectedLanguages.length}/3 language{selectedLanguages.length > 1 ? 's' : ''} selected
              {selectedLanguages.length === 3 && ' — maximum reached'}
            </p>
          )}
        </div>

        {/* ── Section 3: Duration Filters ── */}
        <div className="bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-1 text-gray-700">
            Duration Filters <span className="text-red-500">*</span>
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Select which video lengths to include in the search.
          </p>
          <div className="flex flex-wrap gap-4">
            {DURATION_OPTIONS.map(({ value, label }) => (
              <label
                key={value}
                className={`flex items-center gap-2 px-4 py-2 rounded border cursor-pointer transition text-sm ${
                  selectedDurations.includes(value)
                    ? 'bg-blue-50 border-blue-500 text-blue-700 font-medium'
                    : 'border-gray-200 text-gray-600 hover:border-blue-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedDurations.includes(value)}
                  onChange={() => toggleDuration(value)}
                  className="accent-blue-600"
                  disabled={loading}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* ── Section 4: Signals ── */}
        <div className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-700">
              Signals <span className="text-red-500">*</span>
            </h2>
            {keywordCount > 0 && (
              <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                {keywordCount} signal{keywordCount > 1 ? 's' : ''} entered
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Enter one signal per line. Each signal will be searched across all selected languages and duration filters.
          </p>
          <textarea
            value={keywordsText}
            onChange={(e) => setKeywordsText(e.target.value)}
            placeholder={`MG Astor Review India\nBMW High Beam Assist\nElectric SUV India\nHyundai Creta 2026`}
            rows={10}
            className="w-full border border-gray-300 rounded px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
            disabled={loading}
          />
          <p className="mt-2 text-xs text-gray-400">
            💡 Enter signals separated by new lines or commas. Duplicates are removed automatically.
          </p>
        </div>

        {/* ── Section 5: Excluded Channels (Optional) ── */}
        <div className="bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-1 text-gray-700">
            Excluded Channels <span className="text-gray-400 font-normal text-sm">(Optional)</span>
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Paste YouTube channel URLs to exclude their videos from results.
          </p>
          <div className="space-y-2">
            {excludedChannels.map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={url}
                  onChange={(e) => handleChannelChange(index, e.target.value)}
                  placeholder="https://www.youtube.com/@ChannelName"
                  className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
                {excludedChannels.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeChannelRow(index)}
                    className="text-red-500 hover:text-red-700 px-2 py-1 text-sm font-medium"
                    disabled={loading}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addChannelRow}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
            disabled={loading}
          >
            + Add Another Channel
          </button>
        </div>

        {/* ── Config Summary ── */}
        {keywordCount > 0 && selectedLanguages.length > 0 && selectedDurations.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-green-800 mb-2">📊 Campaign Summary</p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Signals</p>
                <p className="font-bold text-gray-800 text-lg">{keywordCount}</p>
              </div>
              <div>
                <p className="text-gray-500">Languages</p>
                <p className="font-bold text-gray-800 text-lg">{selectedLanguages.length}</p>
              </div>
              <div>
                <p className="text-gray-500">Duration Filters</p>
                <p className="font-bold text-gray-800 text-lg">{selectedDurations.length}</p>
              </div>
            </div>
          </div>
        )}

        {/* ── Buttons ── */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={handleClear}
            className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors text-sm"
            disabled={loading}
          >
            Clear Form
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors shadow-sm text-sm"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Campaign'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default YTCampaignCreate;